import { useEffect, useMemo, useRef, useState } from 'react'
import { fetchOrders } from 'actions/OrderActions'
import { fetchPlaceAncestorsById } from 'actions/PlaceActions'
import { fetchProperty } from 'actions/PropertyActions'
import { fetchUserRecentSearches } from 'actions/SearchActions'
import { getMinOffers } from 'actions/OfferActions'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useOfferList from 'hooks/Offers/useOfferList'
import { MAX_CAROUSEL_ITEM_COUNT } from 'home/components/OfferCarousels/common/constants'
import { areOrdersBeingFetched, haveOrdersBeenFetched } from 'lib/order/orderUtils'
import { getBrowserTimeZone, getCalendarDateStringInTimeZone } from 'lib/datetime/dateUtils'
import { getRecentSearchHotelCarouselDecision } from 'home/selectors/recentSearchHotelCarousel'
import {
  applyRecentSearchHotelInventory,
  buildRecentSearchHotelDatesFilter,
  buildRecentSearchHotelOfferFilters,
  DISABLED_RECENT_SEARCH_HOTEL_DECISION,
  getRecentSearchHotelClockDelay,
  nextRecentSearchHotelOfferBatchSize,
  type RecentSearchHotelCarouselDecision,
} from 'home/recentSearchHotelCarousel/recentSearchHotelCarouselModel'

const EMPTY_OFFERS: Array<{ id: string, available: boolean | undefined }> = []
const EMPTY_EXCLUDED_OFFER_IDS: Array<string> = []

export interface RecentSearchHotelCarouselState {
  decision: RecentSearchHotelCarouselDecision
  filters?: App.OfferListFilters
  selectedDatesAndRoomsFilter?: App.OfferListFilters
}

function useRecentSearchHotelCarousel(
  excludedOfferIds = EMPTY_EXCLUDED_OFFER_IDS,
  experimentEnabled = false,
): RecentSearchHotelCarouselState {
  const dispatch = useAppDispatch()
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const searches = useAppSelector(state => state.search.userRecentSearches)
  const ordersFetched = useAppSelector(state => haveOrdersBeenFetched(state))
  const ordersFetching = useAppSelector(state => areOrdersBeingFetched(state))
  const ordersError = useAppSelector(state => state.orders.error)
  const [now, setNow] = useState(() => Date.now())
  const ordersAttemptMemberId = useRef<string | undefined>(undefined)
  const [ordersFetchFailed, setOrdersFetchFailed] = useState(false)
  const recordedAncestorFailures = useRef(false)

  useEffect(() => {
    if (!experimentEnabled) return
    setNow(Date.now())
  }, [experimentEnabled, searches.cacheKey, searches.state, searches.data, searches.requestGeneration])

  const today = useMemo(
    () => getCalendarDateStringInTimeZone(new Date(now), getBrowserTimeZone()),
    [now],
  )

  const searchDecision = useAppSelector(state => (
    experimentEnabled
      ? getRecentSearchHotelCarouselDecision(state, now, today)
      : DISABLED_RECENT_SEARCH_HOTEL_DECISION
  ))

  useEffect(() => {
    if (!experimentEnabled) return
    const refresh = () => setNow(Date.now())
    const onVisibility = () => {
      if (document.visibilityState === 'visible') refresh()
    }
    window.addEventListener('focus', refresh)
    document.addEventListener('visibilitychange', onVisibility)
    const delay = getRecentSearchHotelClockDelay(now, searchDecision.context?.searchedAt)
    const timer = window.setTimeout(refresh, delay)
    return () => {
      window.removeEventListener('focus', refresh)
      document.removeEventListener('visibilitychange', onVisibility)
      window.clearTimeout(timer)
    }
  }, [experimentEnabled, now, searchDecision.context?.searchedAt])

  useEffect(() => {
    if (!experimentEnabled || !memberId) return
    dispatch(fetchUserRecentSearches())
  }, [dispatch, experimentEnabled, memberId])

  useEffect(() => {
    if (!experimentEnabled || !memberId) {
      ordersAttemptMemberId.current = undefined
      setOrdersFetchFailed(false)
      return
    }
    if (searchDecision.reason !== 'orders_loading') return
    if (ordersFetched) {
      setOrdersFetchFailed(false)
      return
    }
    if (ordersFetching) {
      ordersAttemptMemberId.current = memberId
      return
    }
    if (ordersAttemptMemberId.current === memberId) {
      if (ordersError) setOrdersFetchFailed(true)
      return
    }
    ordersAttemptMemberId.current = memberId
    // Temporary purchase signal. This streams the full order history and enriches
    // payments and reservations. Upcoming orders are a different list and do not
    // complete this fetch. Replace with a compact eligibility summary when one exists.
    dispatch(fetchOrders())
  }, [
    dispatch,
    experimentEnabled,
    memberId,
    ordersError,
    ordersFetched,
    ordersFetching,
    searchDecision.reason,
  ])

  const decisionBeforeInventory = useMemo(() => {
    if (searchDecision.reason !== 'orders_loading' || !ordersFetchFailed) return searchDecision
    return {
      ...searchDecision,
      status: 'suppressed' as const,
      reason: 'data_error' as const,
    }
  }, [ordersFetchFailed, searchDecision])

  useEffect(() => {
    if (!experimentEnabled) return
    decisionBeforeInventory.unresolvedBookingPlaceIds.forEach(placeId => {
      dispatch(fetchPlaceAncestorsById(placeId))
    })
  }, [decisionBeforeInventory.unresolvedBookingPlaceIds, dispatch, experimentEnabled])

  useEffect(() => {
    if (!experimentEnabled || recordedAncestorFailures.current) return
    if (
      decisionBeforeInventory.reason === 'searches_loading'
      || decisionBeforeInventory.reason === 'orders_loading'
    ) return
    recordedAncestorFailures.current = true
    decisionBeforeInventory.failedAncestorPlaceIds.forEach(placeId => {
      dispatch(fetchPlaceAncestorsById(placeId, { retryFailed: true }))
    })
  }, [
    decisionBeforeInventory.failedAncestorPlaceIds,
    decisionBeforeInventory.reason,
    dispatch,
    experimentEnabled,
  ])

  useEffect(() => {
    if (!experimentEnabled) return
    decisionBeforeInventory.unresolvedPropertyIds.forEach(propertyId => {
      dispatch(fetchProperty(propertyId))
    })
  }, [decisionBeforeInventory.unresolvedPropertyIds, dispatch, experimentEnabled])

  const filters = useMemo(() => {
    if (!experimentEnabled) return undefined
    if (!decisionBeforeInventory.context || decisionBeforeInventory.reason !== 'inventory_pending') return undefined
    if (decisionBeforeInventory.context.dateMode === 'flexible') return undefined
    return buildRecentSearchHotelOfferFilters(decisionBeforeInventory.context)
  }, [decisionBeforeInventory.context, decisionBeforeInventory.reason, experimentEnabled])

  const offerList = useOfferList(filters, {
    disabled: !filters,
    minRequiredOffers: MAX_CAROUSEL_ITEM_COUNT,
    filterSoldOut: false,
    filterUnpriced: false,
  })

  const offerMetaData = useAppSelector(state => {
    if (!offerList.key) return undefined
    return state.offer.searchResultMetadata.offerMetaData[offerList.key]
  })

  const requiresExactStay = decisionBeforeInventory.context?.dateMode === 'specific'
  const inventoryOffers = useMemo(() => {
    if (offerList.offerIds.length === 0 && !offerMetaData) return EMPTY_OFFERS
    const excludedIds = new Set(excludedOfferIds)
    return offerList.offerIds.flatMap(id => (
      excludedIds.has(id) ? [] : [{ id, available: offerMetaData?.[id]?.available }]
    ))
  }, [excludedOfferIds, offerList.offerIds, offerMetaData])
  const verifiedOfferCount = useMemo(() => (
    inventoryOffers.filter(offer => (
      requiresExactStay ? offer.available === true : offer.available !== false
    )).length
  ), [inventoryOffers, requiresExactStay])

  useEffect(() => {
    const nextBatchSize = nextRecentSearchHotelOfferBatchSize({
      rawOfferCount: offerList.offerIds.length,
      verifiedOfferCount,
      streamEnded: !!offerList.streamEnded,
      fetching: !!offerList.fetching,
      listening: offerList.socketListenersStatus === 'listening',
      hasError: !!offerList.error,
    })
    if (!filters || !offerList.key || nextBatchSize === undefined) return
    dispatch(getMinOffers({ offerListKey: offerList.key, minOffers: nextBatchSize }))
  }, [
    dispatch,
    filters,
    offerList.error,
    offerList.fetching,
    offerList.key,
    offerList.offerIds.length,
    offerList.socketListenersStatus,
    offerList.streamEnded,
    verifiedOfferCount,
  ])

  const decision = useMemo(() => (
    applyRecentSearchHotelInventory(
      decisionBeforeInventory,
      {
        enabled: !!filters,
        fetching: offerList.fetching,
        streamEnded: !!offerList.streamEnded,
        error: !!offerList.error,
        offers: inventoryOffers,
      },
      decisionBeforeInventory.context?.dateMode === 'specific',
    )
  ), [
    decisionBeforeInventory,
    filters,
    inventoryOffers,
    offerList.error,
    offerList.fetching,
    offerList.streamEnded,
  ])

  const selectedDatesAndRoomsFilter = useMemo(() => {
    if (decision.status !== 'eligible' || !decision.context) return undefined
    return buildRecentSearchHotelDatesFilter(decision.context)
  }, [decision.context, decision.status])

  return {
    decision,
    filters: decision.status === 'eligible' ? filters : undefined,
    selectedDatesAndRoomsFilter,
  }
}

export default useRecentSearchHotelCarousel
